import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { I18nProvider } from "@/contexts/I18nProvider";
import { LocationProvider } from "@/contexts/LocationContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { InstallCTA } from "@/components/pwa/InstallCTA";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DebugOverlay } from "@/components/DebugOverlay";
import { ThemeProvider } from "next-themes";
import { AnalyticsProvider } from "@/contexts/AnalyticsContext";
import { useEffect, lazy, Suspense, ComponentType } from "react";
import { supabase } from "@/integrations/supabase/safeClient";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Lazy load with retry logic for dynamic import failures.
 * On iOS Safari, dynamic imports can sometimes fail due to network issues
 * or aggressive caching. This wrapper retries once before giving up.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      console.error('[LazyLoad] Dynamic import failed, retrying...', error);
      // Wait a bit and retry once
      await new Promise(resolve => setTimeout(resolve, 1000));
      try {
        return await componentImport();
      } catch (retryError) {
        console.error('[LazyLoad] Retry also failed:', retryError);
        // Return a fallback component that shows error and reload button
        return {
          default: (() => {
            // Force reload on next user interaction
            const handleReload = () => window.location.reload();
            return (
              <div className="min-h-screen flex items-center justify-center p-4">
                <div className="text-center space-y-4">
                  <p className="text-muted-foreground">Failed to load the page.</p>
                  <button 
                    onClick={handleReload}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            );
          }) as unknown as T
        };
      }
    }
  });
}

// Lazy load all pages for code splitting with retry logic
const AdminAnalyticsDashboard = lazyWithRetry(() => import('./pages/AdminAnalyticsDashboard'));
const AdminEventDashboard = lazyWithRetry(() => import('./pages/AdminEventDashboard'));
const AdminTrustDashboard = lazyWithRetry(() => import('./pages/AdminTrustDashboard'));
const AdminReportsDashboard = lazyWithRetry(() => import('./pages/AdminReportsDashboard'));
const AdminForumLabelsDashboard = lazyWithRetry(() => import('./pages/AdminForumLabelsDashboard'));
const TrustScoreInfo = lazyWithRetry(() => import('./pages/TrustScoreInfo'));
const FeedbackGuide = lazyWithRetry(() => import('./pages/FeedbackGuide'));
const EventCalendar = lazyWithRetry(() => import('./pages/EventCalendar'));
const Index = lazyWithRetry(() => import("./pages/Index"));
const LocalLife = lazyWithRetry(() => import("./pages/LocalLife"));
const PostDetail = lazyWithRetry(() => import("./pages/PostDetail"));
const NearMe = lazyWithRetry(() => import("./pages/NearMe"));
const Chat = lazyWithRetry(() => import("./pages/Chat"));
const ChatDetail = lazyWithRetry(() => import("./pages/ChatDetail"));
const MyGem = lazyWithRetry(() => import("./pages/MyGem"));
const MyListings = lazyWithRetry(() => import("./pages/MyListings"));
const MyReports = lazyWithRetry(() => import("./pages/MyReports"));
const Watchlist = lazyWithRetry(() => import("./pages/Watchlist"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const ListingDetail = lazyWithRetry(() => import("./pages/ListingDetail"));
const MyComments = lazyWithRetry(() => import("./pages/MyComments"));
const SetYourHood = lazyWithRetry(() => import("./pages/SetYourHood"));
const AdminAdDashboard = lazyWithRetry(() => import("./pages/AdminAdDashboard"));
const BusinessAdSubmission = lazyWithRetry(() => import("./pages/BusinessAdSubmission"));
const SmsOptIn = lazyWithRetry(() => import("./pages/SmsOptIn"));
const DealsHistory = lazyWithRetry(() => import("./pages/DealsHistory"));
const FavoriteEvents = lazyWithRetry(() => import("./pages/FavoriteEvents"));
const MeetingDetail = lazyWithRetry(() => import("./pages/MeetingDetail"));
const MeetingFeedback = lazyWithRetry(() => import("./pages/MeetingFeedback"));
const MeetingQRScreen = lazyWithRetry(() => import("./components/MeetingQRScreen"));
const MeetGroupDetail = lazyWithRetry(() => import("./pages/MeetGroupDetail"));
const MeetEventDetail = lazyWithRetry(() => import("./pages/MeetEventDetail"));
const Install = lazyWithRetry(() => import("./pages/Install"));
const ManageAccount = lazyWithRetry(() => import("./pages/ManageAccount"));
const MyPurchases = lazyWithRetry(() => import("./pages/MyPurchases"));
const LinkedInBackground = lazyWithRetry(() => import("./pages/LinkedInBackground"));
const SurveyPreview = lazyWithRetry(() => import("./pages/SurveyPreview"));
const TutorialDemo = lazyWithRetry(() => import("./pages/TutorialDemo"));
const DarkModePreview = lazyWithRetry(() => import("./pages/DarkModePreview"));
const MyGemPreviewUncompleted = lazyWithRetry(() => import("./pages/MyGemPreviewUncompleted"));

// Lazy load auth page (only needed when not authenticated)
const Auth = lazyWithRetry(() => import("./pages/Auth"));
// Keep NotFound as regular import since it's lightweight
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function AppContent() {
  const { user } = useAuth();
  
  // Enable notifications
  useNotifications();

  // Global cleanup for all realtime channels on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 App: Cleaning up all realtime channels');
      supabase.removeAllChannels();
    };
  }, []);


  return (
    <>
      <main id="main-content">
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/install" element={<Install />} />
          <Route path="/linkedin-bg" element={<LinkedInBackground />} />
          <Route path="/sms-opt-in" element={<SmsOptIn />} />
          <Route path="/survey" element={<SurveyPreview />} />
          <Route path="/tutorial-demo" element={<TutorialDemo />} />
          <Route path="/dark-mode-preview" element={<DarkModePreview />} />
          <Route path="/my-gem-preview" element={<MyGemPreviewUncompleted />} />
          <Route path="/set-your-hood" element={<ProtectedRoute><SetYourHood /></ProtectedRoute>} />
          <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
          <Route path="/local-life" element={<ProtectedRoute><LocalLife /></ProtectedRoute>} />
          <Route path="/local-life/:id" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
          <Route path="/meet/:id" element={<ProtectedRoute><MeetGroupDetail /></ProtectedRoute>} />
          <Route path="/meet/:id/event/:eventId" element={<ProtectedRoute><MeetEventDetail /></ProtectedRoute>} />
          <Route path="/near-me" element={<ProtectedRoute><NearMe /></ProtectedRoute>} />
          <Route path="/near-me/favorites" element={<ProtectedRoute><FavoriteEvents /></ProtectedRoute>} />
          <Route path="/near-me/calendar" element={<ProtectedRoute><EventCalendar /></ProtectedRoute>} />
          <Route path="/admin/events" element={<ProtectedRoute><AdminEventDashboard /></ProtectedRoute>} />
          <Route path="/deals" element={<ProtectedRoute><DealsHistory /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="/chat/:id" element={<ProtectedRoute><ChatDetail /></ProtectedRoute>} />
          <Route path="/meeting/:id" element={<ProtectedRoute><MeetingDetail /></ProtectedRoute>} />
          <Route path="/meeting/:id/qr" element={<ProtectedRoute><MeetingQRScreen /></ProtectedRoute>} />
          <Route path="/meeting/:id/feedback" element={<ProtectedRoute><MeetingFeedback /></ProtectedRoute>} />
           <Route path="/review-demo" element={<ProtectedRoute><MeetingFeedback /></ProtectedRoute>} />
          <Route path="/my-gem" element={<ProtectedRoute><MyGem /></ProtectedRoute>} />
          <Route path="/my-gem/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />
          <Route path="/my-gem/my-purchases" element={<ProtectedRoute><DealsHistory /></ProtectedRoute>} />
          <Route path="/my-gem/my-reports" element={<ProtectedRoute><MyReports /></ProtectedRoute>} />
          <Route path="/my-gem/watchlist" element={<ProtectedRoute><Watchlist /></ProtectedRoute>} />
          <Route path="/my-gem/my-comments" element={<ProtectedRoute><MyComments /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/settings/account" element={<ProtectedRoute><ManageAccount /></ProtectedRoute>} />
          <Route path="/listing/:id" element={<ProtectedRoute><ListingDetail /></ProtectedRoute>} />
          <Route path="/admin/ads" element={<ProtectedRoute><AdminAdDashboard /></ProtectedRoute>} />
          <Route path="/admin/trust" element={<ProtectedRoute><AdminTrustDashboard /></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute><AdminReportsDashboard /></ProtectedRoute>} />
          <Route path="/admin/forum-labels" element={<ProtectedRoute><AdminForumLabelsDashboard /></ProtectedRoute>} />
          <Route path="/trust-score" element={<ProtectedRoute><TrustScoreInfo /></ProtectedRoute>} />
          <Route path="/feedback-guide" element={<ProtectedRoute><FeedbackGuide /></ProtectedRoute>} />
          <Route path="/business/advertise" element={<ProtectedRoute><BusinessAdSubmission /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalyticsDashboard /></ProtectedRoute>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {import.meta.env.PROD && <InstallCTA />}
    </>
  );
}

/**
 * Loading fallback component with timeout message
 */
function LoadingFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="text-center space-y-4">
        <div className="h-8 w-8 animate-spin rounded-full spinner-gradient mx-auto" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <I18nProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              {import.meta.env.PROD && <PWAUpdatePrompt />}
              <BrowserRouter future={{ v7_relativeSplatPath: true }}>
                <AuthProvider>
                  <RealtimeProvider>
                    <LocationProvider>
                      <Suspense fallback={<LoadingFallback />}>
                        <AnalyticsProvider>
                          <AppContent />
                        </AnalyticsProvider>
                      </Suspense>
                      <DebugOverlay />
                    </LocationProvider>
                  </RealtimeProvider>
                </AuthProvider>
              </BrowserRouter>
            </TooltipProvider>
          </QueryClientProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
